/**
 * The workspace's CMMD plan: its allowances, and what is left of them.
 *
 * One query, not two. This used to also fetch a per-day series to draw a
 * burn-down chart beside the figures. The chart went when the plan moved into a
 * card on Connected subscriptions: it answered "was it spent evenly", where the
 * card answers "how much is left", and there is no room in a card for the
 * weaker question.
 *
 * @module usage/useBilling
 */
import type { CmmdBillingContext } from "@cmmd-forge/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { cmmdApiClient } from "~/environments/cmmdApi";

import { useCmmdForgeRuntimeListSnapshot } from "../cmmd/cmmdForgeRuntimeListResource";
import { resolveWorkspaceOrganizationId } from "./workspaceOrganization";

const BILLING_QUERY_ROOT = "cmmd-billing" as const;

export interface BillingView {
  readonly billing: CmmdBillingContext | null;
  readonly isPending: boolean;
  /** A refetch is in flight over figures already on screen. */
  readonly isRefreshing: boolean;
  /** Set when the plan could not be read; the card has no figures to show. */
  readonly error: string | null;
  readonly refresh: () => void;
}

export function useBilling(): BillingView {
  const queryClient = useQueryClient();
  // The plan belongs to an organization. The Shell signs in as the user, so a
  // read that names none lands in their personal space; the catalog says where
  // their Environment runs. The org sits in the key so a catalog that arrives
  // after the first read refetches for the right workspace. Not gated on the
  // catalog: it seeds from a local cache, and a stuck catalog must not blank
  // the card (FOR-36406).
  const catalog = useCmmdForgeRuntimeListSnapshot();
  const organizationId = resolveWorkspaceOrganizationId(catalog);

  const plan = useQuery({
    queryKey: [BILLING_QUERY_ROOT, "plan", organizationId ?? "default"],
    queryFn: () =>
      cmmdApiClient.getBilling(organizationId === undefined ? undefined : { organizationId }),
    // Entitlements change when a subscription changes, not while someone reads
    // the page.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    billing: plan.data ?? null,
    isPending: plan.isPending,
    isRefreshing: plan.isFetching && !plan.isPending,
    error: plan.isError ? "CMMD could not report this workspace's plan." : null,
    refresh: () => {
      void queryClient.invalidateQueries({ queryKey: [BILLING_QUERY_ROOT] });
    },
  };
}
